import { Box, Card, CardContent, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useGetSubmissionsQuery } from "../../services/submissions.service";
import { CHIPS_COLOR, SUBMISSION_STATUS } from "../../types/submissions";
import { useAppTheme } from "../../context/ThemeContext";

// Ordered funnel stages from entry to exit
const FUNNEL_ORDER = Object.values(SUBMISSION_STATUS);

const STATUS_BAR_COLORS: Record<string, string> = {
  initial: "#64748B",
  whatsapped: "#0EA5E9",
  emailed: "#8B5CF6",
  pending: "#EAB308",
  registering: "#F59E0B",
  rejected: "#EF4444",
  accepted: "#059669",
  completed: "#10B981",
};

function SubmissionFunnelWidget() {
  const { data: submissions } = useGetSubmissionsQuery("Submissions");
  const { isNewTheme, accentColor } = useAppTheme();

  const total = submissions?.length ?? 0;

  const counts = (submissions ?? []).reduce<Record<string, number>>((acc, s) => {
    if (s.status) acc[s.status] = (acc[s.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <Card
      sx={
        isNewTheme
          ? {
              width: "100%",
              borderRadius: "8px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
              transition: "box-shadow 0.15s ease",
              "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
            }
          : {
              width: "100%",
              borderRadius: "10px",
              boxShadow: "0px 0px 55px 5px rgba(0, 0, 0, 0.05)",
            }
      }
    >
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6" fontWeight={isNewTheme ? 700 : 600} color={isNewTheme ? "text.primary" : "black"}>
            Submission Funnel
          </Typography>
          {isNewTheme && (
            <Box sx={{ width: 32, height: 3, borderRadius: 999, bgcolor: "#0EA5E9", mt: 0.5 }} />
          )}
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            {total} total
          </Typography>
          <Link to="/submissions" style={{ textDecoration: "none" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.3,
                color: "primary.main",
                fontSize: "0.75rem",
                fontWeight: 600,
                opacity: 0.85,
                "&:hover": { opacity: 1 },
              }}
            >
              View all
              <ArrowForward sx={{ fontSize: 13 }} />
            </Box>
          </Link>
        </Stack>
      </Box>

      <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />

      <CardContent sx={{ px: 2.5, py: 2 }}>
        {total === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center", py: 2, fontStyle: "italic" }}>
            No submissions yet
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)", lg: "repeat(4, 1fr)" },
              gap: 1.5,
            }}
          >
            {FUNNEL_ORDER.map((status) => {
              const count = counts[status] ?? 0;
              if (count === 0) return null;
              const barColor = STATUS_BAR_COLORS[status] ?? "#64748B";
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;

              return (
                <Tooltip key={status} title={`${count} submissions · ${pct}% of total`} arrow>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "8px",
                      border: "1px solid #F1F5F9",
                      bgcolor: "#FAFBFC",
                      transition: "border-color 0.15s, box-shadow 0.15s",
                      "&:hover": {
                        borderColor: barColor + "66",
                        boxShadow: `0 0 0 3px ${barColor}18`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: 4,
                        borderRadius: 999,
                        bgcolor: "#E2E8F0",
                        mb: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${pct}%`,
                          height: "100%",
                          bgcolor: barColor,
                          borderRadius: 999,
                          transition: "width 0.4s ease",
                        }}
                      />
                    </Box>

                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1, color: "#0F172A", mb: 0.5 }}>
                      {count}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.68rem" }}
                    >
                      {status}
                    </Typography>

                    <Typography variant="caption" sx={{ color: barColor, fontWeight: 700, fontSize: "0.65rem" }}>
                      {pct}%
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default SubmissionFunnelWidget;
